(() => {
    const rootSelector = '[data-hd-collection-infinite]';
    const productsSelector = '#main-collection-products';
    const fallbackSelector = '.hd-collection-pagination-fallback, .pagination-wrapper';
    const nextSelector = '.pagination__item--next[href]';
    const preloadDistance = 700;
    const states = new WeakMap();
    const activeStates = new Set();

    const getNextUrl = (container) => {
        const nextLink = container?.querySelector(nextSelector);
        const href = nextLink?.getAttribute('href');

        if (!href || href === '#') {
            return '';
        }

        return new URL(href, window.location.href).href;
    };

    const setLoading = (state, isLoading) => {
        state.loading = isLoading;
        state.container.classList.toggle('is-loading', isLoading);
        const loader = state.container.querySelector('[data-hd-collection-infinite-loader]');

        if (loader) {
            loader.hidden = !isLoading;
        }
    };

    const showFallback = (state) => {
        state.container.classList.add('has-error');
        const fallback = state.container.querySelector(fallbackSelector);

        if (fallback) {
            fallback.classList.add('is-visible-fallback');
        }
    };

    const cleanup = (state, removeSentinel = false) => {
        state.observer?.disconnect();
        window.removeEventListener('scroll', state.checkDistance);
        window.removeEventListener('resize', state.checkDistance);
        state.observer = null;
        activeStates.delete(state);

        if (removeSentinel) {
            state.sentinel?.remove();
        }
    };

    const setComplete = (state) => {
        state.container.classList.add('is-complete');
        const complete = state.container.querySelector('[data-hd-collection-infinite-complete]');

        if (complete) {
            complete.hidden = false;
        }

        cleanup(state, true);
    };

    const replacePagination = (state, nextRoot) => {
        const currentPagination = state.container.querySelector(fallbackSelector);
        const nextPagination = nextRoot.querySelector(fallbackSelector);

        if (currentPagination && nextPagination) {
            currentPagination.replaceWith(nextPagination);
        } else if (currentPagination) {
            currentPagination.remove();
        } else if (nextPagination) {
            const status = state.container.querySelector('.hd-collection-infinite__status');
            state.container.insertBefore(nextPagination, status || state.sentinel);
        }
    };

    const loadNextPage = async (state) => {
        if (state.loading || !state.container.isConnected) {
            return;
        }

        const requestedUrl = getNextUrl(state.container);

        if (!requestedUrl) {
            setComplete(state);
            return;
        }

        if (state.requestedUrls.has(requestedUrl)) {
            showFallback(state);
            cleanup(state, true);
            return;
        }

        state.requestedUrls.add(requestedUrl);
        setLoading(state, true);

        try {
            const response = await fetch(requestedUrl, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) {
                throw new Error(`Collection page request failed: ${response.status}`);
            }

            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const nextRoot = doc.querySelector(rootSelector);
            const currentList = state.container.querySelector(productsSelector);
            const nextList = nextRoot?.querySelector(productsSelector);

            if (!state.container.isConnected) {
                cleanup(state, true);
                return;
            }

            if (!nextRoot || !currentList || !nextList) {
                throw new Error('Collection page markup was not found in the response.');
            }

            const followingUrl = getNextUrl(nextRoot);

            if (followingUrl === requestedUrl) {
                throw new Error('Collection pagination did not advance.');
            }

            const existingIds = new Set(
                Array.from(currentList.children)
                    .map((item) => item.dataset.productId)
                    .filter(Boolean)
            );
            const fragment = document.createDocumentFragment();
            let appendedCount = 0;

            Array.from(nextList.children).forEach((item) => {
                const productId = item.dataset.productId;

                if (productId && existingIds.has(productId)) {
                    return;
                }

                if (productId) {
                    existingIds.add(productId);
                }

                fragment.appendChild(item);
                appendedCount += 1;
            });

            if (appendedCount === 0) {
                throw new Error('Collection page contained no new products.');
            }

            currentList.appendChild(fragment);
            replacePagination(state, nextRoot);

            window.HDCardShippingCountdown?.render?.(currentList);
            currentList.dispatchEvent(
                new CustomEvent('hd:collection:products-appended', {
                    bubbles: true,
                    detail: { count: appendedCount, url: requestedUrl }
                })
            );

            if (!getNextUrl(state.container)) {
                setComplete(state);
            }
        } catch (error) {
            state.failed = true;
            console.warn('[Hobidabi] Infinite scroll stopped; pagination fallback restored.', error);
            showFallback(state);
            cleanup(state, true);
        } finally {
            if (state.container.isConnected) {
                setLoading(state, false);
            }
        }
    };

    const appendNextPage = (state) => {
        if (state.pending) return state.pending;
        state.pending = loadNextPage(state).finally(() => { state.pending = null; });
        return state.pending;
    };

    const titleFacets = (title) => {
        const text = title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ı/g, 'i').toLowerCase();
        const size = [...text.matchAll(/\b(\d+(?:[.,]\d+)?)\s*(mm|cm)\b/g)]
            .map((match) => `${match[1].replace('.', ',')} ${match[2]}`);
        const pack = [...text.matchAll(/\b(\d+)\s*(adet|takim)\b/g)]
            .map((match) => `${match[1]} ${match[2] === 'takim' ? 'takım' : 'adet'}`);
        const colors = [
            ['antik sari', 'Antik sarı'], ['altin sari', 'Altın sarı'], ['antik bakir', 'Antik bakır'],
            ['mat siyah', 'Mat siyah'], ['siyah', 'Siyah'], ['beyaz', 'Beyaz'],
            ['inox', 'İnox'], ['krom', 'Krom'], ['saten', 'Saten'], ['nikel', 'Nikel'],
            ['gri', 'Gri'], ['gumus', 'Gümüş'], ['altin', 'Altın'], ['bakir', 'Bakır'], ['antik', 'Antik']
        ];
        const color = [];
        const matched = [];
        colors.forEach(([token, label]) => {
            if (new RegExp(`\\b${token}\\b`).test(text) && !matched.some((value) => value.includes(token))) {
                color.push(label);
                matched.push(token);
            }
        });
        return { size: [...new Set(size)], color, pack: [...new Set(pack)] };
    };

    const initRefinement = (state) => {
        const panel = state.container.querySelector('[data-hd-refinement]');
        if (!panel || !('fetch' in window) || !('DOMParser' in window)) return;
        const status = panel.querySelector('[data-hd-refinement-status]');
        const fieldset = panel.querySelector('fieldset');
        const selects = [...panel.querySelectorAll('[data-hd-facet]')];
        const list = state.container.querySelector(productsSelector);
        let items = [];
        let preparing = false;
        let ready = false;
        panel.hidden = false;

        const apply = () => {
            if (!ready) return;
            let count = 0;
            items.forEach(({ element, facets }) => {
                const matches = selects.every((select) => !select.value || facets[select.dataset.hdFacet].includes(select.value));
                element.hidden = !matches;
                if (matches) count += 1;
            });
            status.textContent = status.dataset.result.replace('{count}', count).replace('{total}', items.length);
            if (!count) status.textContent += ` ${status.dataset.empty}`;
            const complete = state.container.querySelector('[data-hd-collection-infinite-complete]');
            if (complete) complete.hidden = selects.some((select) => select.value);
        };

        panel.addEventListener('toggle', async () => {
            if (!panel.open || ready || preparing) return;
            preparing = true;
            cleanup(state, true);
            status.textContent = status.dataset.loading;
            panel.setAttribute('aria-busy', 'true');
            if (state.pending) await state.pending;
            while (state.container.isConnected && getNextUrl(state.container) && !state.failed) {
                await appendNextPage(state);
            }
            if (!state.container.isConnected) return;
            panel.removeAttribute('aria-busy');
            preparing = false;
            if (state.failed) {
                status.textContent = status.dataset.error;
                return;
            }
            items = [...list.children].filter((element) => element.dataset.productId).map((element) => {
                const title = element.querySelector('.item-title')?.textContent?.trim() || '';
                const facets = titleFacets(title);
                Object.keys(facets).forEach((key) => {
                    if (!facets[key].length) facets[key] = [status.dataset.unknown];
                });
                return { element, facets };
            });
            selects.forEach((select) => {
                const values = [...new Set(items.flatMap((item) => item.facets[select.dataset.hdFacet]))]
                    .sort((a, b) => a.localeCompare(b, document.documentElement.lang || 'tr', { numeric: true }));
                values.forEach((value) => select.add(new Option(value, value)));
            });
            ready = true;
            fieldset.disabled = false;
            apply();
        });
        panel.addEventListener('change', apply);
        panel.querySelector('[data-hd-refinement-clear]').addEventListener('click', () => {
            selects.forEach((select) => { select.value = ''; });
            apply();
        });
    };

    const initContainer = (container) => {
        if (states.has(container)) {
            return;
        }

        const products = container.querySelector(productsSelector);

        if (!products) {
            return;
        }

        const state = {
            container,
            sentinel: null,
            observer: null,
            loading: false,
            ticking: false,
            requestedUrls: new Set(),
            checkDistance: null,
            pending: null,
            failed: false
        };

        states.set(container, state);
        initRefinement(state);
        container.classList.add('is-enhanced');

        if (!('fetch' in window) || !('DOMParser' in window)) {
            showFallback(state);
            return;
        }

        if (!getNextUrl(container)) {
            setComplete(state);
            return;
        }

        const sentinel = document.createElement('div');
        sentinel.className = 'hd-collection-infinite__sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        container.appendChild(sentinel);
        state.sentinel = sentinel;

        state.checkDistance = () => {
            if (state.ticking || state.loading || !state.sentinel?.isConnected) {
                return;
            }

            state.ticking = true;
            window.requestAnimationFrame(() => {
                state.ticking = false;

                if (state.sentinel?.getBoundingClientRect().top < window.innerHeight + preloadDistance) {
                    appendNextPage(state);
                }
            });
        };

        if ('IntersectionObserver' in window) {
            state.observer = new IntersectionObserver(
                (entries) => {
                    if (entries.some((entry) => entry.isIntersecting)) {
                        appendNextPage(state);
                    }
                },
                { rootMargin: `${preloadDistance}px 0px` }
            );
            state.observer.observe(sentinel);
        }

        window.addEventListener('scroll', state.checkDistance, { passive: true });
        window.addEventListener('resize', state.checkDistance, { passive: true });
        activeStates.add(state);
        state.checkDistance();
    };

    const init = () => {
        activeStates.forEach((state) => {
            if (!state.container.isConnected) {
                cleanup(state, true);
            }
        });
        document.querySelectorAll(rootSelector).forEach(initContainer);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    const mutationObserver = new MutationObserver(init);
    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
    });
})();

